import { useEffect, useRef } from 'react';
import type { TelegramAuthPayload } from '../hooks/useAuth';

interface Props {
  botUsername: string;
  onAuth: (user: TelegramAuthPayload) => void;
  size?: 'small' | 'medium' | 'large';
  requestAccess?: boolean;
}

declare global {
  interface Window {
    __tgOnAuth?: (user: TelegramAuthPayload) => void;
  }
}

/**
 * Telegram Login Widget с callback-вариантом (data-onauth).
 * Не использует data-auth-url (редирект) — данные приходят через JS-callback,
 * что позволяет cookie корректно установиться при последующем fetch() в same-origin.
 *
 * Скрипт лениво вставляется через useEffect и очищается при unmount,
 * что предотвращает дублирование iframe при ремаунте React-компонента.
 */
export function TelegramLoginWidget({
  botUsername,
  onAuth,
  size = 'large',
  requestAccess = false,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    // Глобальный callback. Имя должно быть валидным JS-идентификатором.
    const cbName = `__tg_onauth_${Date.now()}`;
    (window as unknown as Record<string, unknown>)[cbName] = onAuth;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', size);
    script.setAttribute('data-userpic', 'false');
    script.setAttribute('data-onauth', `${cbName}(user)`);
    if (requestAccess) script.setAttribute('data-request-access', 'write');

    const container = ref.current;
    container.appendChild(script);

    return () => {
      delete (window as unknown as Record<string, unknown>)[cbName];
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [botUsername, onAuth, size, requestAccess]);

  return <div ref={ref} className="flex justify-center" />;
}
